import { useEffect, useState } from "react";
import Select from "react-select";
import Creatable from "react-select/creatable";
import { GoogleMap, LoadScript, Marker } from "@react-google-maps/api";
import { getAllVehicleTypes } from "../../../actions/admin/vehicletype";
import {
  AdminFormCard,
  AdminFormFullRow,
  AdminField,
  AdminTextField,
  AdminSelectField,
  AdminToggleField,
} from "../_components/AdminForm";
import { asText, asOnOff } from "../components/ChangeSummary";

export const GARAGE_LIST = "/admin/garagelist";

const listLabels = (options) =>
  (options || []).map((option) => option.label).join(", ") || "None";

// The fields both garage forms write, in the order they show them.
export const GARAGE_FIELDS = [
  { name: "name", label: "Name", format: asText },
  { name: "prefecture", label: "Prefecture tags", format: listLabels },
  { name: "availableVehicleTypes", label: "Available vehicle types", format: listLabels },
  { name: "lat", label: "Latitude", format: asText },
  { name: "lon", label: "Longitude", format: asText },
  { name: "isActive", label: "Active", format: asOnOff },
  { name: "defaultGarage", label: "Default garage", format: asOnOff },
];

const TOKYO = { lat: 35.682846, lng: 139.769026 };

/**
 * The garage form body, shared by the create and edit screens.
 *
 * The two were line-for-line duplicates apart from their initial values — including
 * the map wiring, which is the fiddly part — so a fix to one silently left the other
 * behind. The screens now own only their data loading, confirmation and save.
 */
const GarageForm = ({ formData, setFormData }) => {
  const [vehicleTypeOptions, setVehicleTypeOptions] = useState([]);

  useEffect(() => {
    let cancelled = false;

    getAllVehicleTypes().then((types) => {
      if (cancelled) return;
      setVehicleTypeOptions(types.map((v) => ({ value: v._id, label: v.typename })));
    });

    return () => {
      cancelled = true;
    };
  }, []);

  const { name, prefecture, availableVehicleTypes, lat, lon, isActive, defaultGarage } = formData;

  const onChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  // The map is centred on the coordinates in the form, so typing into either field
  // moves it. It is deliberately NOT wired to the map's own `center_changed` event:
  // feeding the map's centre back in as its `center` loops on float drift.
  const hasPosition = lat !== "" && lon !== "" && !isNaN(Number(lat)) && !isNaN(Number(lon));
  const position = hasPosition ? { lat: Number(lat), lng: Number(lon) } : TOKYO;

  const onMapClick = (e) =>
    setFormData({ ...formData, lon: e.latLng.lng(), lat: e.latLng.lat() });

  const onChangePrefecture = (selections) =>
    setFormData({
      ...formData,
      // Stored lowercased so the prefecture a garage is tagged with matches however
      // the booking flow spells it.
      prefecture: (selections || []).map((element) => ({
        label: element.label.toLowerCase(),
        value: element.value.toLowerCase(),
      })),
    });

  return (
    <>
      <AdminFormCard title="Garage details" columns={2}>
        <AdminFormFullRow>
          <AdminTextField
            name="name"
            label="Name"
            placeholder="Enter garage name"
            value={name}
            onChange={onChange}
            required
          />
        </AdminFormFullRow>

        <AdminSelectField
          name="prefecture"
          label="Prefecture tags"
          placeholder="Type a tag name and press enter..."
          component={Creatable}
          isClearable
          isMulti
          options={[]}
          value={prefecture}
          onChange={onChangePrefecture}
        />

        <AdminSelectField
          name="availableVehicleTypes"
          label="Available vehicle types"
          placeholder="Available vehicle type(s)"
          component={Select}
          isMulti
          options={vehicleTypeOptions}
          value={availableVehicleTypes}
          onChange={(selections) =>
            setFormData({ ...formData, availableVehicleTypes: selections || [] })
          }
        />
      </AdminFormCard>

      <AdminFormCard
        title="Location"
        description="Type the coordinates, or click the map to set them."
        columns={2}
      >
        <AdminTextField
          name="lat"
          label="Latitude"
          type="number"
          step="any"
          placeholder="Enter latitude"
          value={lat}
          onChange={onChange}
          required
        />

        <AdminTextField
          name="lon"
          label="Longitude"
          type="number"
          step="any"
          placeholder="Enter longitude"
          value={lon}
          onChange={onChange}
          required
        />

        <AdminFormFullRow>
          <AdminField label="Map">
            <div className="tw:overflow-hidden tw:rounded-[5px] tw:border tw:border-[#D9D9D9]">
              <LoadScript googleMapsApiKey={process.env.REACT_APP_GOOGLE_API}>
                <GoogleMap
                  mapContainerStyle={{ height: "400px", width: "100%" }}
                  center={position}
                  zoom={13}
                  onClick={onMapClick}
                >
                  {hasPosition && <Marker position={position} />}
                </GoogleMap>
              </LoadScript>
            </div>
          </AdminField>
        </AdminFormFullRow>
      </AdminFormCard>

      <AdminFormCard title="Garage status">
        <AdminToggleField
          name="isActive"
          label="Active"
          description="Only active garages are considered when a vehicle is dispatched."
          checked={isActive}
          onChange={() => setFormData({ ...formData, isActive: !isActive })}
        />

        <AdminToggleField
          name="defaultGarage"
          label="Default garage"
          description="Used when no other garage covers the pickup."
          checked={defaultGarage}
          onChange={() => setFormData({ ...formData, defaultGarage: !defaultGarage })}
        />
      </AdminFormCard>
    </>
  );
};

export default GarageForm;
